import { useEffect, useMemo, useState } from 'react';

import { fetchProductDetails } from '../shared/feed-service';
import { buildNuqliumProductQuery } from '../shared/logic';

/**
 * Fetch outfit products by product IDs, with abort + stable deps.
 *
 * @param {Object} params
 * @param {Array<string|number>} params.prodIds
 * @param {boolean} params.enabled
 * @returns {{ products: Array|null, loading: boolean, error: Error|null }}
 */
const useOutfitProducts = ({ prodIds = [], enabled = true } = {}) => {
  const [products, setProducts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const normalizedIds = useMemo(
    () => (Array.isArray(prodIds) ? prodIds.filter(Boolean).map(String) : []),
    [prodIds]
  );

  const requestKey = useMemo(() => normalizedIds.join('|'), [normalizedIds]);

  useEffect(() => {
    if (!enabled) {
      setProducts(null);
      setLoading(false);
      setError(null);
      return;
    }

    if (!requestKey) {
      setProducts(null);
      setLoading(false);
      setError(null);
      return;
    }

    const ac = new AbortController();
    let isMounted = true;

    const run = async () => {
      setLoading(true);
      setError(null);

      try {
        const payload = buildNuqliumProductQuery(normalizedIds);
        const resp = await fetchProductDetails(
          'nq-fa-product-feed',
          payload,
          { signal: ac.signal },
          'product'
        );

        const list = resp?.data?.products?.products || null;

        if (isMounted) {
          setProducts(list);
        }
      } catch (err) {
        if (err?.name === 'AbortError') return;

        if (isMounted) {
          setProducts(null);
          setError(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      isMounted = false;
      ac.abort();
    };
  }, [enabled, requestKey, normalizedIds]);

  return { products, loading, error };
};

export default useOutfitProducts;
